import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type CurrentUser } from '../api/client';

interface AuthContextType {
  user: CurrentUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    api
      .getCurrentUser()
      .then((u) => {
        if (u.authenticated) {
          setUser(u);
        } else {
          setUser(null);
        }
      })
      .catch((err) => {
        console.error('Auth check failed:', err);
        setUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const logout = () => {
    // Clear local state and redirect to backend logout endpoint
    // The backend will clear the session cookie and redirect to login
    setUser(null);
    window.location.href = '/api/auth/logout?redirect_uri=/login';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user?.authenticated,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
